import React, { useState, useEffect } from 'react';
import { dealsAPI, clientsAPI, contactsAPI, partnersAPI, servicePillarsAPI, pipelineStagesAPI } from '../../utils/api';
import { formatApiError } from '../../utils/errorFormatter';
import { X, Loader2, Plus, Trash2 } from 'lucide-react';

const DealModal = ({ isOpen, onClose, deal = null, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [stages, setStages] = useState([]);
  
  // Dynamic year values - array of {year, value} objects
  const [yearlyValues, setYearlyValues] = useState([
    { year: new Date().getFullYear(), value: '' }
  ]);
  
  const [formData, setFormData] = useState({
    project_title: '',
    client_id: '',
    primary_contact_id: '',
    channel: 'Direct',
    partner_id: '',
    deal_type: 'SBH',
    source: 'Network',
    service_pillars: [],
    total_value: '',
    duration: '',
    no_of_resources: '',
    stage: 'Lead',
    bd_owner_id: '',
    quarter: 'Q1',
    year: new Date().getFullYear(),
    po_status: '',
    remarks: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
      if (deal) {
        setFormData({
          project_title: deal.project_title || '',
          client_id: deal.client_id || '',
          primary_contact_id: deal.primary_contact_id || '',
          channel: deal.channel || 'Direct',
          partner_id: deal.partner_id || '',
          deal_type: deal.deal_type || 'SBH',
          source: deal.source || 'Network',
          service_pillars: deal.service_pillars || [],
          total_value: deal.total_value || '',
          duration: deal.duration || '',
          no_of_resources: deal.no_of_resources || '',
          stage: deal.stage || 'Lead',
          bd_owner_id: deal.bd_owner_id || '',
          quarter: deal.quarter || 'Q1',
          year: deal.year || new Date().getFullYear(),
          po_status: deal.po_status || '',
          remarks: deal.remarks || ''
        });
        
        // Load existing yearly values
        const existingYearlyValues = deal.yearly_values || [];
        if (existingYearlyValues.length > 0) {
          setYearlyValues(existingYearlyValues);
        } else {
          // Backwards compatibility: convert old value_2024, value_2025, value_2026 format
          const legacyValues = [];
          if (deal.value_2022) legacyValues.push({ year: 2022, value: deal.value_2022 });
          if (deal.value_2023) legacyValues.push({ year: 2023, value: deal.value_2023 });
          if (deal.value_2024) legacyValues.push({ year: 2024, value: deal.value_2024 });
          if (deal.value_2025) legacyValues.push({ year: 2025, value: deal.value_2025 });
          if (deal.value_2026) legacyValues.push({ year: 2026, value: deal.value_2026 });
          if (deal.value_2027) legacyValues.push({ year: 2027, value: deal.value_2027 });
          
          if (legacyValues.length > 0) {
            setYearlyValues(legacyValues);
          } else {
            setYearlyValues([{ year: deal.year || new Date().getFullYear(), value: deal.total_value || '' }]);
          }
        }
      } else {
        // Reset for new deal
        setYearlyValues([{ year: new Date().getFullYear(), value: '' }]);
      }
    }
  }, [isOpen, deal]);

  const fetchDropdownData = async () => {
    try {
      const [clientsRes, partnersRes, pillarsRes, stagesRes] = await Promise.all([
        clientsAPI.getAll(),
        partnersAPI.getAll(),
        servicePillarsAPI.getAll(),
        pipelineStagesAPI.getAll()
      ]);
      
      setClients(clientsRes.data);
      setPartners(partnersRes.data.filter(p => p.active));
      setPillars(pillarsRes.data);
      setStages(stagesRes.data);
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    }
  };

  useEffect(() => {
    if (formData.client_id) {
      contactsAPI.getAll({ client_id: formData.client_id })
        .then(res => setContacts(res.data))
        .catch(err => console.error('Error fetching contacts:', err));
    }
  }, [formData.client_id]);

  // Calculate total value from yearly values
  useEffect(() => {
    const total = yearlyValues.reduce((sum, yv) => {
      const val = parseFloat(yv.value) || 0;
      return sum + val;
    }, 0);
    setFormData(prev => ({ ...prev, total_value: total || '' }));
  }, [yearlyValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePillarToggle = (pillarCode) => {
    setFormData(prev => ({
      ...prev,
      service_pillars: prev.service_pillars.includes(pillarCode)
        ? prev.service_pillars.filter(p => p !== pillarCode)
        : [...prev.service_pillars, pillarCode]
    }));
  };

  // Yearly values handlers
  const addYearlyValue = () => {
    // Find the next year that's not already used
    const usedYears = yearlyValues.map(yv => yv.year);
    let nextYear = new Date().getFullYear();
    while (usedYears.includes(nextYear)) {
      nextYear++;
    }
    setYearlyValues([...yearlyValues, { year: nextYear, value: '' }]);
  };

  const removeYearlyValue = (index) => {
    if (yearlyValues.length > 1) {
      setYearlyValues(yearlyValues.filter((_, i) => i !== index));
    }
  };

  const updateYearlyValue = (index, field, value) => {
    const updated = [...yearlyValues];
    updated[index] = { ...updated[index], [field]: field === 'year' ? parseInt(value) : value };
    setYearlyValues(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validation
      if (!formData.project_title) throw new Error('Project title is required');
      if (!formData.client_id) throw new Error('Client is required');
      if (formData.service_pillars.length === 0) throw new Error('At least one service pillar is required');
      if (formData.channel === 'Partner' && !formData.partner_id) throw new Error('Partner is required when channel is Partner');

      // Calculate total from yearly values
      const totalValue = yearlyValues.reduce((sum, yv) => sum + (parseFloat(yv.value) || 0), 0);
      if (totalValue <= 0) throw new Error('Total value must be greater than 0');

      // Build payload with yearly_values array
      const payload = {
        ...formData,
        total_value: totalValue,
        yearly_values: yearlyValues.map(yv => ({
          year: parseInt(yv.year),
          value: parseFloat(yv.value) || 0
        })).filter(yv => yv.value > 0),
        // Also set individual year fields for backwards compatibility
        value_2022: null,
        value_2023: null,
        value_2024: null,
        value_2025: null,
        value_2026: null,
        value_2027: null,
        no_of_resources: formData.no_of_resources ? parseInt(formData.no_of_resources) : null,
        year: parseInt(formData.year)
      };

      // Set individual year values for backwards compatibility
      yearlyValues.forEach(yv => {
        const yearKey = `value_${yv.year}`;
        if (yv.year >= 2022 && yv.year <= 2027) {
          payload[yearKey] = parseFloat(yv.value) || null;
        }
      });

      if (deal) {
        await dealsAPI.update(deal._id, payload);
      } else {
        await dealsAPI.create(payload);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err, 'Failed to save deal'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Generate year options (2020 to 2035)
  const yearOptions = [];
  for (let y = 2020; y <= 2035; y++) {
    yearOptions.push(y);
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="deal-modal-overlay"
    >
      <div 
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="deal-modal"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-slate-900">
            {deal ? 'Edit Deal' : 'Create New Deal'}
          </h2>
          <button 
            type="button"
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
            data-testid="deal-modal-close"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Project Title *</label>
                <input
                  type="text"
                  name="project_title"
                  value={formData.project_title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  data-testid="deal-project-title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  data-testid="deal-client-select"
                >
                  <option value="">Select Client</option>
                  {clients.map(c => (
                    <option key={c._id} value={c._id}>{c.client_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Primary Contact</label>
                <select
                  name="primary_contact_id"
                  value={formData.primary_contact_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={!formData.client_id}
                >
                  <option value="">Select Contact</option>
                  {contacts.map(c => (
                    <option key={c._id} value={c._id}>{c.full_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Channel *</label>
                <select
                  name="channel"
                  value={formData.channel}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Direct">Direct</option>
                  <option value="Partner">Partner</option>
                </select>
              </div>

              {formData.channel === 'Partner' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Partner *</label>
                  <select
                    name="partner_id"
                    value={formData.partner_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="">Select Partner</option>
                    {partners.map(p => (
                      <option key={p._id} value={p._id}>{p.partner_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Deal Type *</label>
                <select
                  name="deal_type"
                  value={formData.deal_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="SBH">SBH (&lt; RM 500k)</option>
                  <option value="Tender">Tender (&gt;= RM 500k)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Source *</label>
                <select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Recurring">Recurring</option>
                  <option value="WOM">Word of Mouth</option>
                  <option value="Network">Network</option>
                  <option value="Referral">Referral</option>
                  <option value="Tender Portal">Tender Portal</option>
                  <option value="Cold Outreach">Cold Outreach</option>
                </select>
              </div>
            </div>
          </div>

          {/* Service Pillars */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Service Pillars *</label>
            <div className="flex flex-wrap gap-2">
              {pillars.map(pillar => (
                <button
                  key={pillar._id}
                  type="button"
                  onClick={() => handlePillarToggle(pillar.pillar_code)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    formData.service_pillars.includes(pillar.pillar_code)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-primary'
                  }`}
                >
                  {pillar.pillar_code} - {pillar.pillar_name}
                </button>
              ))}
            </div>
          </div>

          {/* Financial Information - Dynamic Year Values */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Financial Information</h3>
              <button
                type="button"
                onClick={addYearlyValue}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                data-testid="add-year-btn"
              >
                <Plus size={16} />
                Add Year
              </button>
            </div>
            
            {/* Yearly Values */}
            <div className="space-y-3 mb-4">
              <label className="block text-sm font-medium text-slate-700">Revenue by Year</label>
              {yearlyValues.map((yv, index) => (
                <div key={index} className="flex items-center gap-3" data-testid={`year-value-row-${index}`}>
                  <div className="w-32">
                    <select
                      value={yv.year}
                      onChange={(e) => updateYearlyValue(index, 'year', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid={`year-select-${index}`}
                    >
                      {yearOptions.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">RM</span>
                      <input
                        type="number"
                        value={yv.value}
                        onChange={(e) => updateYearlyValue(index, 'value', e.target.value)}
                        placeholder="0"
                        className="w-full pl-12 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        min="0"
                        data-testid={`year-value-${index}`}
                      />
                    </div>
                  </div>
                  {yearlyValues.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeYearlyValue(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`remove-year-${index}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Total Value Display */}
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-slate-700">Total Deal Value</span>
                <span className="text-xl font-bold text-primary" data-testid="total-value-display">
                  RM {(formData.total_value || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Other Financial Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="e.g., 6 months"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">No. of Resources</label>
                <input
                  type="number"
                  name="no_of_resources"
                  value={formData.no_of_resources}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Pipeline Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Pipeline Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Stage *</label>
                <select
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {stages.map(s => (
                    <option key={s.stage} value={s.stage}>{s.stage} ({s.probability}%)</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Quarter</label>
                <select
                  name="quarter"
                  value={formData.quarter}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Q1">Q1</option>
                  <option value="Q2">Q2</option>
                  <option value="Q3">Q3</option>
                  <option value="Q4">Q4</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Deal Year *</label>
                <select
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                >
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">PO Status</label>
                <input
                  type="text"
                  name="po_status"
                  value={formData.po_status}
                  onChange={handleChange}
                  placeholder="e.g., Pending"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="deal-submit-btn"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              {deal ? 'Update Deal' : 'Create Deal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DealModal;
